import { useState, useEffect, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  BarChart3, Users, DollarSign, TrendingUp, Music2, ShoppingBag, Mail,
  ExternalLink, Radio, Globe, Save, Download, Search, Trash2, Tag, RefreshCw, FileText, ChevronDown
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const RANGES = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
  { value: "365d", label: "Year" },
  { value: "all", label: "All Time" },
];

const GOLD = "#D4AF37";
const RED = "#8B0000";
const PALETTE = ["#D4AF37", "#8B0000", "#f5e6a8", "#c94a4a", "#8b7d3f", "#5c5028", "#c0392b"];

function Kpi({ label, value, sub, icon: Icon, testId, format = "num", accent = false, live = false }) {
  const display =
    format === "money"
      ? `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : format === "pct"
      ? `${Number(value || 0).toFixed(2)}%`
      : Number(value || 0).toLocaleString();
  return (
    <div
      data-testid={testId}
      className={`bg-[#0A0A0A] border rounded-lg p-4 flex flex-col justify-between min-h-[110px] transition-colors ${
        accent ? "border-[#D4AF37]/40 shadow-[0_0_24px_-8px_rgba(212,175,55,0.4)]" : "border-white/5 hover:border-white/10"
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">{label}</p>
        {Icon && <Icon className={`w-3.5 h-3.5 ${accent ? "text-[#D4AF37]" : "text-white/25"}`} />}
      </div>
      <div>
        <p className={`font-heading text-3xl tracking-wide ${accent ? "text-[#D4AF37]" : "text-white"}`}>
          {display}
          {live && Number(value) > 0 && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse align-middle" />}
        </p>
        {sub && <p className="text-white/30 text-[11px] mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

function ChartCard({ title, right, children, testId }) {
  return (
    <div data-testid={testId} className="bg-[#0A0A0A] border border-white/5 rounded-lg p-5">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-xs uppercase tracking-wider text-white/60 font-semibold">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function useAnalyticsData(range) {
  const [state, setState] = useState({
    kpis: null, tsVisitors: [], tsRevenue: [], device: [], browser: [], os: [], referrer: [],
    topSongs: [], topAlbums: [], topProducts: [], topAffiliates: [], topStreaming: [], topPages: [], geo: [],
    loading: true,
  });
  const refresh = async () => {
    try {
      const [kpis, tsV, tsR, dev, brw, os_, ref, songs, albums, prods, affs, str, pages, geo] = await Promise.all([
        api.get("/analytics/kpis"),
        api.get(`/analytics/timeseries?metric=visitors&range=${range}`),
        api.get(`/analytics/timeseries?metric=revenue&range=${range}`),
        api.get(`/analytics/breakdown?by=device&range=${range}`),
        api.get(`/analytics/breakdown?by=browser&range=${range}`),
        api.get(`/analytics/breakdown?by=os&range=${range}`),
        api.get(`/analytics/breakdown?by=referrer_class&range=${range}`),
        api.get(`/analytics/top?of=songs&range=${range}`),
        api.get(`/analytics/top?of=albums&range=${range}`),
        api.get(`/analytics/top?of=products&range=${range}`),
        api.get(`/analytics/top?of=affiliates&range=${range}`),
        api.get(`/analytics/top?of=streaming&range=${range}`),
        api.get(`/analytics/top?of=pages&range=${range}`),
        api.get(`/analytics/geo?range=${range}`),
      ]);
      setState({
        kpis: kpis.data,
        tsVisitors: tsV.data,
        tsRevenue: tsR.data,
        device: dev.data,
        browser: brw.data,
        os: os_.data,
        referrer: ref.data,
        topSongs: songs.data,
        topAlbums: albums.data,
        topProducts: prods.data,
        topAffiliates: affs.data,
        topStreaming: str.data,
        topPages: pages.data,
        geo: geo.data,
        loading: false,
      });
    } catch (e) {
      toast.error("Failed to load analytics");
      setState((s) => ({ ...s, loading: false }));
    }
  };
  useEffect(() => { setState((s)=>({...s, loading:true})); refresh(); }, [range]);
  return { ...state, refresh };
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 transition-colors ${
        active ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/40 hover:text-white/70"
      }`}
    >
      {children}
    </button>
  );
}

// ── Tabs ──

function OverviewTab({ data, range }) {
  const k = data.kpis || {};
  const tsData = data.tsVisitors.map((v, i) => ({ ...v, revenue: (data.tsRevenue[i] || {}).value || 0 }));
  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Live Now" value={k.live_visitors} icon={Radio} testId="kpi-live" live accent />
        <Kpi label="Today" value={k.visitors_today} icon={Users} testId="kpi-today" />
        <Kpi label="This Week" value={k.visitors_week} icon={Users} testId="kpi-week" />
        <Kpi label="This Month" value={k.visitors_month} icon={Users} testId="kpi-month" />
        <Kpi label="Total Revenue" value={k.revenue_total} icon={DollarSign} format="money" testId="kpi-revenue" accent />
        <Kpi label="Music Revenue" value={k.revenue_music} icon={Music2} format="money" testId="kpi-revenue-music" />
        <Kpi label="Merch Revenue" value={k.revenue_merch} icon={ShoppingBag} format="money" testId="kpi-revenue-merch" />
        <Kpi label="Conversion" value={k.conversion_rate} icon={TrendingUp} format="pct" testId="kpi-conversion" />
        <Kpi label="Subscribers" value={k.subscribers} icon={Mail} testId="kpi-subscribers" />
        <Kpi label="Affiliate Clicks" value={k.affiliate_clicks} icon={ExternalLink} testId="kpi-affiliate-clicks" />
        <Kpi label="Lifetime Visitors" value={k.visitors_lifetime} icon={Users} testId="kpi-lifetime" />
        <Kpi label="Unique Visitors" value={k.unique_lifetime} icon={Users} testId="kpi-unique" />
      </div>

      {/* Top-line spotlight */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-[#D4AF37]/30 rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/70 mb-1">Top Selling Song</p>
          <p className="font-heading text-xl">{k.top_song?.title || "—"}</p>
          <p className="text-white/40 text-xs mt-1">{k.top_song ? `${k.top_song.count} sales · $${k.top_song.revenue}` : "No sales yet"}</p>
        </div>
        <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-[#8B0000]/30 rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-wider text-[#c94a4a] mb-1">Top Selling Product</p>
          <p className="font-heading text-xl">{k.top_product?.title || "—"}</p>
          <p className="text-white/40 text-xs mt-1">{k.top_product ? `${k.top_product.count} sales · $${k.top_product.revenue}` : "No sales yet"}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title={`Visitors — ${range}`} testId="chart-visitors">
          <div style={{ width: "100%", height: 240, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <LineChart data={tsData}>
                <CartesianGrid stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="date" stroke="#ffffff40" fontSize={10} />
                <YAxis stroke="#ffffff40" fontSize={10} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #ffffff20", fontSize: 12 }} />
                <Line type="monotone" dataKey="value" stroke={GOLD} strokeWidth={2} dot={false} name="Visitors" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title={`Revenue — ${range}`} testId="chart-revenue">
          <div style={{ width: "100%", height: 240, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <LineChart data={tsData}>
                <CartesianGrid stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="date" stroke="#ffffff40" fontSize={10} />
                <YAxis stroke="#ffffff40" fontSize={10} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #ffffff20", fontSize: 12 }} formatter={(v) => `$${v}`} />
                <Line type="monotone" dataKey="revenue" stroke={RED} strokeWidth={2} dot={false} name="Revenue" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <BreakdownPie title="Devices" data={data.device} testId="chart-device" />
        <BreakdownPie title="Traffic Sources" data={data.referrer} testId="chart-referrer" />
        <BreakdownPie title="Browsers" data={data.browser} testId="chart-browser" />
        <BreakdownPie title="Operating Systems" data={data.os} testId="chart-os" />
      </div>
    </div>
  );
}

function BreakdownPie({ title, data, testId }) {
  const nonEmpty = (data || []).filter((d) => d.value > 0);
  return (
    <ChartCard title={title} testId={testId}>
      {nonEmpty.length === 0 ? (
        <p className="text-white/25 text-xs py-8 text-center">No data yet.</p>
      ) : (
        <div style={{ width: "100%", height: 160, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={140}>
            <PieChart>
              <Pie data={nonEmpty} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={60}>
                {nonEmpty.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #ffffff20", fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#ffffff70" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
}

function TopList({ title, items, keyLabel, valueLabel, valueFn, testId }) {
  return (
    <ChartCard title={title} testId={testId}>
      {(!items || items.length === 0) ? (
        <p className="text-white/25 text-xs py-6 text-center">No data yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-white/25 text-xs font-mono w-4">{i + 1}</span>
                <span className="text-white truncate">{it[keyLabel] || "—"}</span>
              </div>
              <span className="text-[#D4AF37] font-semibold flex-shrink-0 text-xs">{valueFn ? valueFn(it) : it[valueLabel]}</span>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

function ContentTab({ data }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TopList title="Top Songs" items={data.topSongs} keyLabel="title" valueFn={(x) => `${x.sales} · $${x.revenue}`} testId="top-songs" />
        <TopList title="Top Albums / EPs" items={data.topAlbums} keyLabel="title" valueFn={(x) => `${x.sales} · $${x.revenue}`} testId="top-albums" />
        <TopList title="Top Products" items={data.topProducts} keyLabel="title" valueFn={(x) => `${x.sales} · $${x.revenue}`} testId="top-products" />
        <TopList title="Top Pages" items={data.topPages} keyLabel="path" valueLabel="views" testId="top-pages" />
        <TopList title="Streaming Clicks" items={data.topStreaming} keyLabel="platform" valueLabel="clicks" testId="top-streaming" />
        <TopList title="Top Affiliate Clicks" items={data.topAffiliates} keyLabel="title" valueLabel="clicks" testId="top-affiliates" />
      </div>
      <ChartCard title="Geography (Country · State · City)" testId="chart-geo">
        {(!data.geo || data.geo.length === 0) ? (
          <p className="text-white/25 text-xs py-6 text-center">Geographic data will appear once visitors' regions are captured.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {data.geo.map((g, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-[#111] p-2 rounded">
                <span className="text-white/70">
                  {g.country}
                  {g.state && <span className="text-white/40"> · {g.state}</span>}
                  {g.city && <span className="text-white/40"> · {g.city}</span>}
                </span>
                <span className="text-[#D4AF37] font-semibold">{g.value}</span>
              </div>
            ))}
          </div>
        )}
      </ChartCard>
    </div>
  );
}

function SubscribersTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/subscribers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(data);
    } catch { toast.error("Failed to load subscribers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const debounce = useRef();
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(load, 300);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const exportCsv = () => {
    const token = sessionStorage.getItem("access_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/subscribers/export.csv`;
    // Fetch with Authorization then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "subscribers.csv";
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error("Export failed"));
  };

  const addTag = async (id, currentTags) => {
    const tag = window.prompt("New tag:");
    if (!tag) return;
    try {
      await api.put(`/analytics/subscribers/${id}`, { tags: [...(currentTags || []), tag.trim()] });
      toast.success("Tag added");
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this subscriber?")) return;
    try { await api.delete(`/analytics/subscribers/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-5">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="relative flex-1 min-w-0 max-w-md">
          <Search className="w-4 h-4 text-white/25 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="subscriber-search"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            placeholder="Search email, source page, tag…"
            className="w-full bg-[#111] border border-white/10 pl-9 pr-3 py-2 rounded text-sm text-white outline-none focus:border-[#D4AF37]"
          />
        </div>
        <Button data-testid="export-subscribers-csv" onClick={exportCsv} className="bg-[#D4AF37] text-black hover:bg-white text-xs">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>
      {loading ? <p className="text-white/30 text-sm">Loading…</p> : rows.length === 0 ? (
        <p className="text-white/25 text-sm text-center py-10">No subscribers {q ? "match your search" : "yet"}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 text-[10px] uppercase tracking-wider border-b border-white/10">
                <th className="text-left py-2 pr-3">Email</th>
                <th className="text-left py-2 pr-3">Source</th>
                <th className="text-left py-2 pr-3">Tags</th>
                <th className="text-left py-2 pr-3">Consent</th>
                <th className="text-left py-2 pr-3">Date</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`subscriber-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2 pr-3 text-white">{r.email}</td>
                  <td className="py-2 pr-3 text-white/40 text-xs">{r.source_page || "—"}</td>
                  <td className="py-2 pr-3 text-white/50 text-xs">
                    {(r.tags || []).map((t, i) => (
                      <span key={i} className="inline-block bg-white/5 px-1.5 py-0.5 rounded text-[10px] mr-1">{t}</span>
                    ))}
                  </td>
                  <td className="py-2 pr-3 text-white/50 text-xs">{r.marketing_consent ? "yes" : "no"}</td>
                  <td className="py-2 pr-3 text-white/40 text-xs">{(r.subscribed_at || "").split("T")[0]}</td>
                  <td className="py-2 text-right">
                    <Button variant="ghost" size="icon" onClick={()=>addTag(r.id, r.tags)} className="text-white/40 hover:text-[#D4AF37] h-7 w-7"><Tag className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={()=>remove(r.id)} className="text-white/40 hover:text-red-400 h-7 w-7"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IntegrationsTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/analytics/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);
  const setF = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/analytics/config", cfg); toast.success("Tracking IDs saved. Reload public site to see effect."); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!cfg) return <p className="text-white/30 text-sm">Loading…</p>;

  const F = ({ label, id, placeholder, help }) => (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">{label}</label>
      <input
        data-testid={`cfg-${id}`}
        value={cfg[id] || ""}
        onChange={(e) => setF(id, e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 font-mono"
      />
      {help && <p className="text-white/25 text-[10px] mt-1">{help}</p>}
    </div>
  );

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6">
      <div className="max-w-2xl space-y-5">
        <div>
          <h3 className="font-heading text-xl">Tracking Integrations</h3>
          <p className="text-white/40 text-xs mt-1">Paste your tracking IDs. Scripts and verification tags are auto-injected site-wide after you save.</p>
        </div>
        <F label="Google Analytics 4 · Measurement ID" id="ga4_measurement_id" placeholder="G-XXXXXXXXXX" help="Find in GA4 Admin → Data Streams → your web stream." />
        <F label="Google Tag Manager · Container ID" id="gtm_container_id" placeholder="GTM-XXXXXXX" help="Find in GTM workspace → Container ID." />
        <F label="Meta Pixel · Pixel ID" id="meta_pixel_id" placeholder="1234567890" help="Events Manager → Pixel Settings." />
        <F label="Microsoft Clarity · Project ID" id="clarity_project_id" placeholder="xxxxxxxxxx" help="Clarity → Settings → Overview → Project ID (heatmaps & recordings)." />
        <F label="Google Search Console · Verification code" id="gsc_verification" placeholder="google-site-verification content value" help="From GSC 'HTML tag' verification method — content= value only." />
        <F label="Facebook Domain · Verification code" id="fb_domain_verification" placeholder="facebook-domain-verification content value" help="Business Manager → Domains → Verify with meta tag." />

        <Button data-testid="save-analytics-config" onClick={save} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-white">
          <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save Tracking IDs"}
        </Button>
      </div>
    </div>
  );
}

// ── PDF Export helper (loaded lazily to keep dashboard boot fast) ──
async function exportPDF(kpis, range) {
  try {
    const [{ default: jsPDF }, autoTableModule] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);
    // eslint-disable-next-line no-unused-vars
    const _ = autoTableModule.default;
    const doc = new jsPDF({ unit: "pt" });
    doc.setFillColor(5, 5, 5); doc.rect(0, 0, doc.internal.pageSize.getWidth(), 60, "F");
    doc.setTextColor(212, 175, 55); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
    doc.text("Luxifer 6 — Analytics Report", 40, 38);
    doc.setTextColor(180, 180, 180); doc.setFontSize(10);
    doc.text(`Range: ${range} · Generated: ${new Date().toLocaleString()}`, 40, 55);
    const k = kpis || {};
    const rows = [
      ["Live now", k.live_visitors],
      ["Visitors today", k.visitors_today],
      ["Visitors this week", k.visitors_week],
      ["Visitors this month", k.visitors_month],
      ["Visitors lifetime", k.visitors_lifetime],
      ["Unique visitors lifetime", k.unique_lifetime],
      ["Newsletter subscribers", k.subscribers],
      ["Affiliate clicks", k.affiliate_clicks],
      ["Total revenue", `$${(k.revenue_total || 0).toFixed(2)}`],
      ["Music revenue", `$${(k.revenue_music || 0).toFixed(2)}`],
      ["Merch revenue", `$${(k.revenue_merch || 0).toFixed(2)}`],
      ["Orders total", k.orders_total],
      ["Conversion rate", `${(k.conversion_rate || 0).toFixed(2)}%`],
      ["Top selling song", k.top_song?.title || "—"],
      ["Top selling product", k.top_product?.title || "—"],
    ];
    doc.autoTable({
      startY: 80,
      head: [["Metric", "Value"]],
      body: rows,
      styles: { fontSize: 10, textColor: 30 },
      headStyles: { fillColor: [212, 175, 55], textColor: 20 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    doc.save(`luxifer6-analytics-${range}-${new Date().toISOString().slice(0,10)}.pdf`);
  } catch (e) {
    toast.error("PDF export failed: " + (e.message || "unknown"));
  }
}

// ── Main page ──

export default function AnalyticsCommandCenter() {
  const [range, setRange] = useState("30d");
  const [tab, setTab] = useState("overview");
  const data = useAnalyticsData(range);

  const exportOrdersCsv = () => {
    const token = sessionStorage.getItem("access_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/orders/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "orders.csv";
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <div data-testid="analytics-command-center">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-3"><BarChart3 className="w-7 h-7 text-[#D4AF37]" /> COMMAND CENTER</h1>
          <p className="text-white/40 text-sm">Real-time business intelligence for the Luxifer 6 brand</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-0.5 bg-[#111] border border-white/10 rounded p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                data-testid={`range-${r.value}`}
                onClick={() => setRange(r.value)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded transition-colors ${
                  range === r.value ? "bg-[#D4AF37] text-black" : "text-white/50 hover:text-white"
                }`}
              >{r.label}</button>
            ))}
          </div>
          <Button data-testid="refresh-btn" variant="outline" onClick={data.refresh} className="border-white/10 text-white/60 hover:text-white h-9">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button data-testid="export-pdf-btn" onClick={() => exportPDF(data.kpis, range)} className="bg-[#D4AF37] text-black hover:bg-white h-9">
            <FileText className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          <Button data-testid="export-orders-csv-btn" variant="outline" onClick={exportOrdersCsv} className="border-white/10 text-white/60 hover:text-white h-9">
            <Download className="w-3.5 h-3.5 mr-1" /> Orders CSV
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/5 mb-6 overflow-x-auto">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} testId="tab-overview">CEO Overview</TabButton>
        <TabButton active={tab === "content"} onClick={() => setTab("content")} testId="tab-content">Content Analytics</TabButton>
        <TabButton active={tab === "subs"} onClick={() => setTab("subs")} testId="tab-subs">Subscribers</TabButton>
        <TabButton active={tab === "integrations"} onClick={() => setTab("integrations")} testId="tab-integrations">Integrations</TabButton>
      </div>

      {data.loading && tab !== "subs" && tab !== "integrations" ? (
        <p className="text-white/30 text-sm">Loading data…</p>
      ) : tab === "overview" ? (
        <OverviewTab data={data} range={range} />
      ) : tab === "content" ? (
        <ContentTab data={data} />
      ) : tab === "subs" ? (
        <SubscribersTab />
      ) : (
        <IntegrationsTab />
      )}
    </div>
  );
}
